// Codex Desktop wraps some user turns in a harness envelope. The person's
// words are inside it, so a reader shown the envelope verbatim reads XML
// before reading the person. Two envelopes are observed on transcript
// captures:
//
//   <realtime_delegation>   a voice session handing a spoken request to the
//                           text agent: <input> is what was said, and
//                           <transcript_delta> the voice exchange so far;
//                           <source> names a handoff the client made on its
//                           own, such as the flush when the session ends.
//   <send_user_message_question_reply>
//                           the answers to the agent's request_user_input
//                           questions, as a JSON list of question/answer pairs.
//
// The parser is a presentation aid: it never changes the block's text, and it
// answers null for anything that is not one of these envelopes at the start
// of the text, so a plain message that happens to mention a tag is untouched.

const inner = (name, text) => new RegExp(`<${name}>([\\s\\S]*?)<\\/${name}>`).exec(text)?.[1];

// The voice exchange is lines of "user:" / "assistant:" prose; a line that
// names no speaker continues the previous one.
function parseTranscript(delta) {
    const lines = [];
    for (const raw of String(delta || "").split(/\r?\n/)) {
        const spoken = /^(user|assistant):\s*(.*)$/.exec(raw);
        if (spoken) lines.push({ speaker: spoken[1], text: spoken[2] });
        else if (lines.length && raw.trim()) lines[lines.length - 1].text += `\n${raw}`;
    }
    return lines;
}

export function parseCodexUserWrapper(text) {
    if (typeof text !== "string") return null;
    const body = text.replace(/^﻿/, "").trimStart();
    if (body.startsWith("<realtime_delegation>")) {
        return {
            kind: "voice",
            source: (inner("source", body) || "").trim(),
            // An unterminated <input> (the capture clipped the envelope) still
            // yields the words up to the clip.
            input: (inner("input", body) ?? /<input>([\s\S]*)$/.exec(body)?.[1] ?? "").trim(),
            transcript: parseTranscript(inner("transcript_delta", body)),
        };
    }
    const reply = "<send_user_message_question_reply>";
    if (body.startsWith(reply)) {
        const json = (inner("send_user_message_question_reply", body) ?? body.slice(reply.length)).trim();
        let items;
        try { items = JSON.parse(json); } catch { return null; }
        if (!Array.isArray(items)) return null;
        return {
            kind: "question_reply",
            items: items.map((item) => ({ question: String(item?.question ?? ""), answer: String(item?.answer ?? "") })),
        };
    }
    return null;
}
